/**
 * Browser-side persistence for the salon showcase.
 *
 * Isolation rules (the reason this file exists):
 *  - One IndexedDB database PER tenant AND PER authenticated account
 *    (`salonNamespace`). Two tenants, or two accounts in the same browser, can
 *    never read or overwrite each other's demonstration data.
 *  - Nothing here touches the ERP: no API calls, no sync queue, no service
 *    worker, no ADR-007 offline-write machinery. It is a local, fictional,
 *    single-purpose store.
 *  - Auth tokens are never stored here (ADR-002 AUTH-3). The record contains
 *    only fictional salon data and UI navigation state.
 *  - `destroy()` deletes the whole database; it runs on explicit logout and on
 *    "reset demo data".
 *
 * Limits (documented, accepted for a demonstration): the data is not
 * encrypted at rest (it is fictional), and concurrent tabs of the same account
 * are last-write-wins.
 */
import type { Role, SalonState } from './types.ts';

export interface NavEntry {
  page: string;
  params: { id?: string };
}
export type NavStacks = Partial<Record<Role, NavEntry[]>>;

export interface PersistedSalon {
  readonly version: number;
  readonly state: SalonState;
  readonly nav: NavStacks;
}

export interface SalonStorage {
  /** `true` when data survives a reload (IndexedDB); `false` for the in-memory fallback. */
  readonly persistent: boolean;
  load(): Promise<PersistedSalon | null>;
  save(record: PersistedSalon): Promise<void>;
  /** Deletes every byte this namespace stored. */
  destroy(): Promise<void>;
  close(): void;
}

const DB_PREFIX = 'sakhtar-salon-demo';
const STORE = 'state';
const RECORD_KEY = 'current';

/** Namespace = tenant + account. Both must be non-empty or storage is refused. */
export function salonNamespace(tenantId: string, principalId: string): string {
  if (!tenantId || !principalId) throw new Error('salon storage needs a tenant and an account');
  return `${DB_PREFIX}::${tenantId}::${principalId}`;
}

export function isPersistedSalon(value: unknown, version: number): value is PersistedSalon {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Partial<PersistedSalon>;
  const st = v.state as Partial<SalonState> | undefined;
  return (
    v.version === version &&
    typeof st === 'object' &&
    st !== null &&
    typeof st.meta === 'object' &&
    Array.isArray(st.appointments) &&
    Array.isArray(st.customers) &&
    Array.isArray(st.payments) &&
    Array.isArray(st.expenses) &&
    Array.isArray(st.commissions) &&
    typeof st.session === 'object'
  );
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexeddb request failed'));
  });
}

/** IndexedDB-backed storage for one namespace. Throws if IndexedDB is unavailable. */
export function indexedDbStorage(namespace: string, factory?: IDBFactory): SalonStorage {
  const idb = factory ?? (typeof indexedDB === 'undefined' ? undefined : indexedDB);
  if (!idb) throw new Error('indexeddb unavailable');
  let dbPromise: Promise<IDBDatabase> | null = null;
  const open = () => {
    dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = idb.open(namespace, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('indexeddb open failed'));
      req.onblocked = () => reject(new Error('indexeddb open blocked'));
    });
    return dbPromise;
  };
  return {
    persistent: true,
    async load() {
      const db = await open();
      const value = await request(
        db.transaction(STORE, 'readonly').objectStore(STORE).get(RECORD_KEY),
      );
      return (value as PersistedSalon | undefined) ?? null;
    },
    async save(record) {
      const db = await open();
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(record, RECORD_KEY);
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('indexeddb write failed'));
        tx.onabort = () => reject(tx.error ?? new Error('indexeddb write aborted'));
      });
    },
    async destroy() {
      if (dbPromise) {
        try {
          (await dbPromise).close();
        } catch {
          // already failed to open — nothing to close
        }
        dbPromise = null;
      }
      await new Promise<void>((resolve, reject) => {
        const req = idb.deleteDatabase(namespace);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error ?? new Error('indexeddb delete failed'));
        req.onblocked = () => resolve();
      });
    },
    close() {
      void dbPromise?.then((db) => db.close()).catch(() => undefined);
      dbPromise = null;
    },
  };
}

/** In-memory storage: tests, and the fallback when IndexedDB is unavailable (private mode). */
export function memoryStorage(): SalonStorage & { snapshot(): PersistedSalon | null } {
  let record: PersistedSalon | null = null;
  return {
    persistent: false,
    load: () => Promise.resolve(record),
    save: (r) => {
      record = r;
      return Promise.resolve();
    },
    destroy: () => {
      record = null;
      return Promise.resolve();
    },
    close: () => undefined,
    snapshot: () => record,
  };
}

/** IndexedDB when available, otherwise the in-memory fallback (never throws). */
export function openSalonStorage(namespace: string): SalonStorage {
  try {
    return indexedDbStorage(namespace);
  } catch {
    return memoryStorage();
  }
}
